import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import { Button, Stack, Typography } from "@mui/material";
import { ContentCard } from "@shared";

import { PortalAccountStatusResponse } from "@/api";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { formatTimestamp } from "@/common/utils/formatTimestamp";
import { useTranslation } from "@/translations";

interface Props {
  status: PortalAccountStatusResponse;
  description: string;
  onResend: () => void;
  /** Present for client subjects only; the button is not rendered otherwise. */
  onWithdraw?: () => void;
  isBusy: boolean;
}

/**
 * The "Einladung ausstehend" card while the subject has not registered yet. The resend button
 * follows the server-computed throttle (max once per 24h); the backend enforces it regardless.
 * Withdrawing is not throttled — it is the way out when the registration cannot be completed, and
 * it puts the access back into its initial state.
 */
export function PortalPendingInvitationCard({ status, description, onResend, onWithdraw, isBusy }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  return (
    <ContentCard title={t("portalAccess.pending.title")} grow={false}>
      <Stack spacing={2} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<EmailOutlinedIcon />}
            disabled={!status.resendAllowed || isBusy}
            onClick={onResend}
          >
            {t("portalAccess.pending.resend")}
          </Button>

          {onWithdraw && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<UndoOutlinedIcon />}
              disabled={isBusy}
              onClick={onWithdraw}
            >
              {t("portalAccess.pending.withdraw")}
            </Button>
          )}

          {!status.resendAllowed && status.resendPossibleAtUtc && (
            <Typography variant="body2" color="text.secondary">
              {t("portalAccess.pending.resendPossibleAt", {
                dateTime: formatTimestamp(status.resendPossibleAtUtc, t, language),
              })}
            </Typography>
          )}
        </Stack>
      </Stack>
    </ContentCard>
  );
}
